import React, { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Zap, Clock, Sparkles, ChevronRight, Chrome, Play, CheckCircle2 } from 'lucide-react';
import PrivacyPolicy from './PrivacyPolicy';

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/nutshell-video/gecpaialjbjooacghlledhahdeepjclm'; // Replace with actual Chrome Web Store URL

function handleChromeStoreClick() {
  (window as any).gtag('event', 'conversion', {
    send_to: 'AW-11262914069/6j6yCIeSpP0bEJXcyfop',
    value: 0.1,
    currency: 'AUD',
  });
}
const VALID_ROUTES = ['/', '/privacy'];

function App() {
  const [currentPage, setCurrentPage] = useState<'home' | 'privacy' | '404'>('home');

  const heroRef = useRef<HTMLDivElement>(null);
  const featuresRef = useRef<HTMLDivElement>(null);
  const howItWorksRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  const featuresInView = useInView(featuresRef, { once: true, margin: '-100px' });
  const howItWorksInView = useInView(howItWorksRef, { once: true, margin: '-100px' });
  const ctaInView = useInView(ctaRef, { once: true, margin: '-100px' });

  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0.3]);

  // Handle pathname-based routing
  useEffect(() => {
    const handlePathChange = () => {
      const path = window.location.pathname;
      if (path === '/privacy') {
        setCurrentPage('privacy');
      } else if (VALID_ROUTES.includes(path)) {
        setCurrentPage('home');
      } else {
        setCurrentPage('404');
      }
    };

    handlePathChange();
    window.addEventListener('popstate', handlePathChange);
    return () => window.removeEventListener('popstate', handlePathChange);
  }, []);

  useEffect(() => {
    if (currentPage === 'home') {
      document.title = 'Nutshell Video - Instant YouTube Summaries';
    } else if (currentPage === 'privacy') {
      document.title = 'Privacy Policy - Nutshell Video';
    } else {
      document.title = 'Page Not Found - Nutshell Video';
    }
    window.scrollTo(0, 0);
  }, [currentPage]);

  const navigateTo = (page: 'home' | 'privacy') => {
    const path = page === 'home' ? '/' : `/${page}`;
    window.history.pushState({}, '', path);
    setCurrentPage(page);
  };

  const features = [
    {
      icon: <Zap size={28} />,
      title: 'Instant Summaries',
      description: 'Get AI-powered summaries in seconds, right in your browser sidebar.',
    },
    {
      icon: <Clock size={28} />,
      title: 'Save Hours',
      description: 'Skip the fluff. Extract key points from any video without watching the whole thing.',
    },
    {
      icon: <Sparkles size={28} />,
      title: 'Smart Insights',
      description: 'Choose your detail level: quick overview, standard breakdown, or deep analysis.',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Install the Extension',
      description: 'Add Nutshell Video to Chrome with one click.',
    },
    {
      number: '02',
      title: 'Pin the Extension',
      description: 'Pin the extension to the Chrome toolbar for ease of access.',
    },
    {
      number: '03',
      title: 'Get Your Summary',
      description:
        'Watch any YouTube video and click the extension icon or press Ctrl+Shift+S (Cmd+Shift+S on Mac). Then watch the magic happen!',
    },
  ];

  if (currentPage === 'privacy') {
    return <PrivacyPolicy onBack={() => navigateTo('home')} />;
  }

  if (currentPage === '404') {
    return (
      <div className="landing">
        <div className="grain" />
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '6rem', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>404</h1>
          <p style={{ fontSize: '1.25rem', color: 'var(--text-secondary)', marginBottom: '2rem' }}>Page not found</p>
          <button onClick={() => navigateTo('home')} className="btn btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="landing">
      {/* Animated background grain */}
      <div className="grain" />

      {/* Navigation */}
      <motion.nav
        className="nav"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="nav-inner">
          <a href="/" className="nav-logo">
            <img src="/nutshell-logo-cropped.png" alt="Nutshell Video" className="nav-logo-img" />
            <span className="nav-logo-text">Nutshell</span>
          </a>
          <a
            href={CHROME_STORE_URL}
            className="nav-cta"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleChromeStoreClick}
          >
            <Chrome size={16} />
            Add to Chrome
          </a>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="hero" ref={heroRef}>
        <motion.div className="hero-content" style={{ y: heroY, opacity: heroOpacity }}>
          <motion.div
            className="hero-badge"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Play size={12} fill="currentColor" />
            <span>Chrome Extension</span>
          </motion.div>

          <motion.h1
            className="hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            YouTube videos,
            <br />
            <span className="hero-title-accent">in a nutshell.</span>
          </motion.h1>

          <motion.p
            className="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            AI-powered summaries that save you hours.
            <br />
            Get the key points without watching the whole video.
          </motion.p>

          <motion.div
            className="hero-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <a
              href={CHROME_STORE_URL}
              className="btn btn-primary"
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleChromeStoreClick}
            >
              <Chrome size={20} />
              Add to Chrome — It&apos;s Free
              <ChevronRight size={18} />
            </a>
          </motion.div>

          <motion.div
            className="hero-stats"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <div className="hero-stat">
              <CheckCircle2 size={16} />
              <span>100% Free</span>
            </div>
            <div className="hero-stat">
              <CheckCircle2 size={16} />
              <span>No Sign-up Required</span>
            </div>
            <div className="hero-stat">
              <CheckCircle2 size={16} />
              <span>Works Instantly</span>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          className="hero-visual"
          initial={{ opacity: 0, y: 60, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="hero-image-wrapper">
            <div className="hero-image-glow" />
            <img src="/promo.png" alt="Nutshell Video in action" className="hero-image" />
          </div>
        </motion.div>

        {/* Decorative elements */}
        <div className="hero-blur hero-blur-1" />
        <div className="hero-blur hero-blur-2" />
        <div className="hero-blur hero-blur-3" />
      </section>

      {/* Features Section */}
      <section className="features" ref={featuresRef}>
        <div className="features-inner">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={featuresInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">Features</span>
            <h2 className="section-title">Everything you need to summarize smarter</h2>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card"
                initial={{ opacity: 0, y: 40 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" ref={howItWorksRef}>
        <div className="how-it-works-inner">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            animate={howItWorksInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="section-label">How It Works</span>
            <h2 className="section-title">Three steps to clarity</h2>
          </motion.div>

          <div className="steps-grid">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                className="step-card"
                initial={{ opacity: 0, y: 40 }}
                animate={howItWorksInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
              >
                <span className="step-number">{step.number}</span>
                <h3 className="step-title">{step.title}</h3>
                <p className="step-description">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta" ref={ctaRef}>
        <motion.div
          className="final-cta-inner"
          initial={{ opacity: 0, y: 40 }}
          animate={ctaInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="final-cta-glow" />
          <img src="/nutshell-logo-cropped.png" alt="" className="final-cta-logo" />
          <h2 className="final-cta-title">Ready to save hours?</h2>
          <p className="final-cta-subtitle">Join thousands who get the essence of any YouTube video in seconds.</p>
          <a
            href={CHROME_STORE_URL}
            className="btn btn-primary btn-large"
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleChromeStoreClick}
          >
            <Chrome size={22} />
            Add Nutshell to Chrome
            <ChevronRight size={20} />
          </a>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <img src="/nutshell-logo-cropped.png" alt="Nutshell Video" className="footer-logo" />
            <span className="footer-name">Nutshell Video</span>
          </div>
          <div className="footer-links">
            <a
              href="/privacy"
              className="footer-link"
              onClick={(e) => {
                e.preventDefault();
                navigateTo('privacy');
              }}
            >
              Privacy Policy
            </a>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} Nutshell Video. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
